import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Camera, Play, CheckCircle2, MessageSquare, Clock, AlertTriangle, Sparkles } from "lucide-react";

export const PRIORITY_META = {
  high: { label: "High", color: "#EF4444" },
  medium: { label: "Medium", color: "#F59E0B" },
  low: { label: "Low", color: "#10B981" },
};
export const STATUS_LABEL = {
  not_started: "Not Started", in_progress: "In Progress",
  waiting_review: "Waiting Review", completed: "Completed",
};

export default function TasksPage() {
  const [data, setData] = useState(null);

  const load = () => api.get("/tasks/dashboard/me").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return <div className="p-10 text-zinc-500">Loading…</div>;
  const { name, shift, today, overdue, completed_today, progress, next_task } = data;

  return (
    <div className="p-4 md:p-10 max-w-3xl mx-auto" data-testid="tasks-page">
      <div className="mb-6">
        <div className="label-eyebrow">My Tasks</div>
        <h1 className="font-display text-3xl md:text-4xl mt-2">Welcome, {name} 👋</h1>
        <div className="flex items-center gap-2 mt-2 text-sm text-zinc-400" data-testid="current-shift">
          <Clock className="w-4 h-4 text-[#D4AF37]" /> Current shift: {shift.start} – {shift.end}
        </div>
      </div>

      <div className="card-lux p-5 mb-5" data-testid="task-progress-card">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-zinc-400">Today's completion</span>
          <span className="text-[#D4AF37] font-medium" data-testid="task-progress-pct">{progress}%</span>
        </div>
        <div className="h-2.5 rounded-full bg-[#1a1a1a] overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#E5C86B] transition-all" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex gap-4 mt-3 text-xs text-zinc-500">
          <span>{today.length} today</span>
          <span className="text-red-400">{overdue.length} overdue</span>
          <span className="text-emerald-400">{completed_today.length} completed</span>
        </div>
      </div>

      {next_task && next_task.status !== "completed" && (
        <div className="card-lux p-5 mb-6 border border-[#D4AF37]/40" data-testid="next-task-card">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#D4AF37] mb-2">
            <Sparkles className="w-3.5 h-3.5" /> Next Recommended Task
          </div>
          <TaskCard task={next_task} onChanged={load} highlight />
        </div>
      )}

      {overdue.length > 0 && (
        <Section title="Overdue" icon={<AlertTriangle className="w-4 h-4 text-red-400" />} testid="overdue-section">
          {overdue.map((t) => <TaskCard key={t.id} task={t} onChanged={load} />)}
        </Section>
      )}

      <Section title="Today's Tasks" testid="today-section">
        {today.filter((t) => t.status !== "completed").map((t) => <TaskCard key={t.id} task={t} onChanged={load} />)}
        {today.filter((t) => t.status !== "completed").length === 0 && (
          <div className="text-sm text-zinc-500 py-4 text-center">All caught up! 🎉</div>
        )}
      </Section>

      {completed_today.length > 0 && (
        <Section title="Completed Today" testid="completed-section">
          {completed_today.map((t) => <TaskCard key={t.id} task={t} onChanged={load} />)}
        </Section>
      )}
    </div>
  );
}

function Section({ title, icon, children, testid }) {
  return (
    <div className="mb-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h2 className="font-display text-lg">{title}</h2>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

export function TaskCard({ task, onChanged, highlight, managerView, staff = [] }) {
  const [comment, setComment] = useState("");
  const [showComments, setShowComments] = useState(false);
  const pm = PRIORITY_META[task.priority] || PRIORITY_META.medium;
  const overdue = task.status !== "completed" && task.due_at && task.due_at < new Date().toISOString();

  const setStatus = async (status) => {
    try {
      const r = await api.patch(`/tasks/${task.id}`, { status });
      if (status === "completed" && r.data.status === "waiting_review") {
        toast.info("Sent for manager review (photo proof task)");
      } else {
        toast.success(`Task ${STATUS_LABEL[r.data.status].toLowerCase()}`);
      }
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const onPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 4 * 1024 * 1024) return toast.error("Photo too large (max 4 MB)");
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/tasks/${task.id}/photos`, { data_url: reader.result });
        toast.success("Photo uploaded");
        onChanged?.();
      } catch (err) { toast.error("Upload failed"); }
    };
    reader.readAsDataURL(f);
  };

  const sendComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/tasks/${task.id}/comments`, { text: comment.trim() });
      setComment("");
      toast.success("Comment added");
      onChanged?.();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className={`rounded-sm bg-[#0a0a0a] border ${highlight ? "border-[#D4AF37]/50" : "border-[#1a1a1a]"} p-4`}
      style={{ borderLeft: `3px solid ${pm.color}` }} data-testid={`task-card-${task.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm text-zinc-100 font-medium">{task.title}</div>
          {task.description && <div className="text-xs text-zinc-500 mt-0.5">{task.description}</div>}
          <div className="flex items-center gap-2 mt-1.5 flex-wrap text-[10px]">
            <span className="px-1.5 py-0.5 rounded-sm border" style={{ color: pm.color, borderColor: `${pm.color}55`, background: `${pm.color}10` }}>{pm.label}</span>
            <span className="text-zinc-500 capitalize">{task.department}</span>
            {managerView && <span className="text-zinc-400">{task.assignee_name}</span>}
            {task.due_at && <span className={overdue ? "text-red-400" : "text-zinc-500"}>Due {task.due_at.slice(0, 16).replace("T", " ")}</span>}
            {task.estimated_minutes ? <span className="text-zinc-600">~{task.estimated_minutes}min</span> : null}
            {task.requires_photo && <span className="text-[#D4AF37] flex items-center gap-0.5"><Camera className="w-3 h-3" /> proof</span>}
            <span className="text-zinc-400">{STATUS_LABEL[task.status]}</span>
          </div>
        </div>
      </div>

      {(task.photos || []).length > 0 && (
        <div className="flex gap-1.5 mt-2 flex-wrap">
          {task.photos.map((p) => <img key={p.id} src={p.data_url} alt="" className="w-12 h-12 object-cover rounded-sm border border-[#2a2a2a]" />)}
        </div>
      )}

      {task.status !== "completed" && !managerView && (
        <div className="flex gap-2 mt-3 flex-wrap">
          {task.status === "not_started" && (
            <Button onClick={() => setStatus("in_progress")} data-testid={`start-task-${task.id}`} className="h-8 text-xs bg-[#D4AF37] text-black hover:bg-[#E5C86B]">
              <Play className="w-3 h-3 mr-1" /> Start
            </Button>
          )}
          {task.status === "in_progress" && (
            <Button onClick={() => setStatus("completed")} data-testid={`complete-task-${task.id}`} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-500 text-white">
              <CheckCircle2 className="w-3 h-3 mr-1" /> {task.requires_photo ? "Submit for Review" : "Mark Done"}
            </Button>
          )}
          {task.status === "waiting_review" && <span className="text-xs text-amber-400 py-1.5">Awaiting manager review…</span>}
          <label className="h-8 px-3 text-xs rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] cursor-pointer flex items-center gap-1">
            <Camera className="w-3 h-3" /> Photo
            <input type="file" accept="image/*" onChange={onPhoto} data-testid={`photo-input-${task.id}`} className="hidden" />
          </label>
          <button onClick={() => setShowComments(!showComments)} data-testid={`comments-toggle-${task.id}`}
            className="h-8 px-3 text-xs rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] flex items-center gap-1">
            <MessageSquare className="w-3 h-3" /> {(task.comments || []).length || ""}
          </button>
        </div>
      )}

      {showComments && (
        <div className="mt-3 space-y-1.5">
          {(task.comments || []).map((c) => (
            <div key={c.id} className="text-xs text-zinc-400 px-2 py-1.5 rounded-sm bg-[#050505] border border-[#1a1a1a]">
              <span className="text-zinc-500">{c.by_name}:</span> {c.text}
            </div>
          ))}
          <div className="flex gap-2">
            <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment…" data-testid={`comment-input-${task.id}`} className="bg-[#050505] border-[#2a2a2a] h-8 text-xs" />
            <Button onClick={sendComment} disabled={!comment.trim()} className="h-8 text-xs bg-[#D4AF37] text-black">Send</Button>
          </div>
        </div>
      )}
    </div>
  );
}
